import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API } from "@/App";
import axios from "axios";
import { 
  TrendingUp, 
  TrendingDown, 
  Award,
  ArrowRight,
  RefreshCw,
  Building2,
  Calendar
} from "lucide-react";

const CATEGORIES = [
  { id: "all", name: "All Funds" },
  { id: "Large Cap", name: "Large Cap" },
  { id: "Mid Cap", name: "Mid Cap" },
  { id: "Small Cap", name: "Small Cap" },
  { id: "ELSS", name: "ELSS Tax Saver" },
  { id: "Hybrid", name: "Hybrid" },
  { id: "Index", name: "Index Funds" }
];

const ReturnBadge = ({ value, label }) => {
  if (!value && value !== 0) return <span className="text-muted-foreground">-</span>;
  const isPositive = value >= 0;
  return (
    <div className="text-center">
      <div className={`text-lg font-bold ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
        {isPositive ? '+' : ''}{value.toFixed(2)}%
      </div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
};

const FundCard = ({ fund, rank }) => {
  return (
    <Card className="bg-card/50 border-border/50 hover:border-primary/50 transition-all">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
            {rank}
          </div>
          <div className="flex-1 min-w-0">
            <Link 
              to={`/research/${fund.schemeCode}`}
              className="font-semibold text-foreground hover:text-primary transition-colors line-clamp-2"
              data-testid={`fund-link-${fund.schemeCode}`}
            >
              {fund.schemeName}
            </Link>
            <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
              <Building2 className="w-3 h-3" />
              <span className="truncate">{fund.fundHouse || 'N/A'}</span>
            </div>
            <div className="inline-block mt-2 px-2 py-1 bg-primary/10 text-primary text-xs rounded">
              {fund.category}
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-4 gap-2 mt-4 pt-4 border-t border-border/50">
          <div className="text-center">
            <div className="text-lg font-bold text-primary">₹{fund.current_nav?.toFixed(2) || '-'}</div>
            <div className="text-xs text-muted-foreground">NAV</div>
          </div>
          <ReturnBadge value={fund.returns1Y} label="1Y" />
          <ReturnBadge value={fund.returns3Y} label="3Y" />
          <ReturnBadge value={fund.returns5Y} label="5Y" />
        </div>
        
        <div className="flex gap-2 mt-4">
          <Link to={`/research/${fund.schemeCode}`} className="flex-1">
            <Button variant="outline" size="sm" className="w-full" data-testid={`view-fund-${fund.schemeCode}`}>
              View Details <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

export const TopPerformingFundsPage = () => {
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortBy, setSortBy] = useState("returns1Y");

  const fetchFunds = useCallback(async (category) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: "20" });
      if (category && category !== "all") {
        params.append("category", category);
      }
      
      const response = await axios.get(`${API}/funds/top-performers?${params}`);
      setFunds(response.data.funds || []);
    } catch (error) {
      console.error("Error fetching funds:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchFunds(selectedCategory);
  }, [selectedCategory, fetchFunds]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchFunds(selectedCategory);
  };

  const sortedFunds = [...funds].sort((a, b) => {
    const aVal = a[sortBy] || 0;
    const bVal = b[sortBy] || 0;
    return bVal - aVal;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="top-performing-funds-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Award className="w-8 h-8 text-primary" />
              Top Performing Funds
            </h1>
            <p className="text-muted-foreground mt-1">
              Best performing mutual funds ranked by returns
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh Data
          </Button>
        </div>

        {/* Category Tabs */}
        <Card className="bg-card/50 border-border/50">
          <CardContent className="p-4">
            <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
              <TabsList className="grid grid-cols-4 md:grid-cols-7 gap-2 bg-transparent h-auto">
                {CATEGORIES.map(cat => (
                  <TabsTrigger 
                    key={cat.id} 
                    value={cat.id}
                    className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                    data-testid={`category-${cat.id}`}
                  >
                    {cat.name}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </CardContent>
        </Card>

        {/* Sort Options */}
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">Sort by:</span>
          <div className="flex gap-2">
            {[
              { id: "returns1Y", label: "1 Year" },
              { id: "returns3Y", label: "3 Year" },
              { id: "returns5Y", label: "5 Year" }
            ].map(opt => (
              <Button
                key={opt.id}
                variant={sortBy === opt.id ? "default" : "outline"}
                size="sm"
                onClick={() => setSortBy(opt.id)}
                data-testid={`sort-${opt.id}`}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Funds Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="bg-card/50 border-border/50">
                <CardContent className="p-4">
                  <div className="animate-pulse space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-muted rounded-full" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-muted rounded w-3/4" />
                        <div className="h-3 bg-muted rounded w-1/2" />
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 pt-4 border-t border-border/50">
                      {[...Array(4)].map((_, j) => (
                        <div key={j} className="space-y-1">
                          <div className="h-5 bg-muted rounded" />
                          <div className="h-3 bg-muted rounded w-1/2 mx-auto" />
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : sortedFunds.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedFunds.map((fund, index) => (
              <FundCard key={fund.schemeCode} fund={fund} rank={index + 1} />
            ))}
          </div>
        ) : (
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-8 text-center">
              <TrendingDown className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold">No funds found</h3>
              <p className="text-muted-foreground mt-2">
                Try selecting a different category or refresh the data.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Calendar className="w-8 h-8 text-primary flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-foreground">Data Source</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Returns are calculated using actual NAV data from AMFI (Association of Mutual Funds in India). 
                  Data is refreshed every 6 hours. Past performance is not indicative of future returns.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
